/**
 * Migration utilities for page and section data
 *
 * Handles versioning and data migration when schema changes.
 */

import { sections } from './index';
import { PAGE_FORMAT_VERSION } from './schema-types';

/**
 * Migrate a single section to the current schema version
 * @param {Object} section - Section data from storage
 * @returns {Object} Migrated section data
 */
export function migrateSection(section) {
  if (!section || !section.type) {
    return section;
  }

  const sectionDef = sections[section.type];
  if (!sectionDef || !sectionDef.schema) {
    // Unknown section type, return as-is
    return section;
  }

  const schema = sectionDef.schema;
  const currentVersion = schema.version || 1;
  const dataVersion = section.version || 0;

  // Already current version
  if (dataVersion >= currentVersion) {
    return section;
  }

  // Migrate content data
  let migratedContent = section.content || {};
  if (typeof schema.migrate === 'function') {
    migratedContent = schema.migrate(migratedContent, dataVersion);
  }

  return {
    ...section,
    version: currentVersion,
    content: migratedContent
  };
}

/**
 * Migrate all sections in a page
 * @param {Array} sections - Array of section data
 * @returns {Array} Migrated sections
 */
export function migrateSections(sectionsData) {
  if (!Array.isArray(sectionsData)) {
    return [];
  }

  return sectionsData.map(migrateSection);
}

/**
 * Migrate a full page structure
 * @param {Object} page - Page data from storage
 * @returns {Object} Migrated page data
 */
export function migratePage(page) {
  if (!page) {
    return page;
  }

  const pageVersion = page.version || 0;

  // Migrate sections
  const migratedSections = migrateSections(page.sections || []);

  return {
    ...page,
    version: PAGE_FORMAT_VERSION,
    sections: migratedSections
  };
}

/**
 * Check if page needs migration
 * @param {Object} page - Page data
 * @returns {boolean}
 */
export function needsMigration(page) {
  if (!page) return false;

  // Check page version
  if ((page.version || 0) < PAGE_FORMAT_VERSION) {
    return true;
  }

  // Check section versions
  if (page.sections) {
    for (const section of page.sections) {
      if (!section || !section.type) continue;

      const sectionDef = sections[section.type];
      if (!sectionDef || !sectionDef.schema) continue;

      const schemaVersion = sectionDef.schema.version || 1;
      const dataVersion = section.version || 0;

      if (dataVersion < schemaVersion) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Get migration info for a page
 * @param {Object} page - Page data
 * @returns {Object} Migration info
 */
export function getMigrationInfo(page) {
  const info = {
    needsMigration: false,
    pageVersionChange: null,
    sectionChanges: []
  };

  if (!page) return info;

  const pageVersion = page.version || 0;
  if (pageVersion < PAGE_FORMAT_VERSION) {
    info.needsMigration = true;
    info.pageVersionChange = {
      from: pageVersion,
      to: PAGE_FORMAT_VERSION
    };
  }

  if (page.sections) {
    for (const section of page.sections) {
      if (!section || !section.type) continue;

      const sectionDef = sections[section.type];
      if (!sectionDef || !sectionDef.schema) continue;

      const schemaVersion = sectionDef.schema.version || 1;
      const dataVersion = section.version || 0;

      if (dataVersion < schemaVersion) {
        info.needsMigration = true;
        info.sectionChanges.push({
          sectionId: section.id,
          sectionType: section.type,
          from: dataVersion,
          to: schemaVersion
        });
      }
    }
  }

  return info;
}
